import React, { useEffect, useState, useRef  } from 'react';
import { Message } from './utils/types';
import api from './api';
import io from 'socket.io-client';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { send } from 'process';
import { getLoggedInUser } from './utils/auth';
import { getInitials } from './utils/getInitials'; 
import ChatInput from './components/ChatInput';
import FileAttachmentCard from './components/FileAttachmentCard';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { Fancybox } from "@fancyapps/ui";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply, faDownload, faPaperPlane } from '@fortawesome/free-solid-svg-icons';



type Props = {
  user: any;
  onLogout: () => void;
};


// 👇 Setup WebSocket connection once
const socket = io('http://localhost:3000');

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface Conversation {
  id: number;
  type: string;
  title: string;
}

function Chat({ user, onLogout }: Props) {
  
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
    }
  }, []);

  
  useEffect(() => {
    Fancybox.bind('[data-fancybox="gallery"]', {
      // Optional custom options
    });

    return () => Fancybox.destroy();
  }, []);

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  
  const login_user = getLoggedInUser();
  const senderId = login_user?.id;
  const user_name = login_user?.name || '';

  const [open, setOpen] = useState(false);
  const initials = getInitials(user_name);

  
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [lastSeenMessageId, setLastSeenMessageId] = useState<number | null>(null);
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const [replyScrollTarget, setReplyScrollTarget] = useState<number | null>(null);

  useEffect(() => {
    if (replyScrollTarget !== null) {
      const el = messageRefs.current[replyScrollTarget];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Highlight effect (optional)
        el.classList.add('bg-black', 'bg-opacity-40');
        setTimeout(() => {
          el.classList.remove('bg-black', 'bg-opacity-40');
          // Reset so next reply click can trigger again
          setReplyScrollTarget(null);
        }, 1000);
      }

      
    }
  }, [replyScrollTarget]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;

    const threshold = 100; // px
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;

    setIsAutoScroll(atBottom);

    if (atBottom) {
      setNewMessagesCount(0); // clear counter
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!messages || messages.length === 0) return;

    const latestMsg = messages[messages.length - 1];

    const latestMsgId = latestMsg.id;
    const fromOtherUser = latestMsg.sender?.id !== senderId;

    if (isAutoScroll) {
      if (replyScrollTarget == null) {
        scrollToBottom();
      }
      
      setNewMessagesCount(0);
      setLastSeenMessageId(latestMsgId);
    } else if (
      fromOtherUser &&
      latestMsgId !== lastSeenMessageId // 👈 avoid recounting same message
    ) {
      setNewMessagesCount((count) => count + 1);
      setLastSeenMessageId(latestMsgId); // 👈 mark it as counted
    }
  }, [messages]);

  // 🧠 Load existing messages & listen for new ones
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!selectedConversationId) return;
    api.get(`/messages/${selectedConversationId}`).then((res) => {
      setMessages(res.data);
    });
    // WebSocket: listen for live messages
    socket.on('newMessage', (msg: Message) => {
      if (msg.conversation.id === selectedConversationId) {
        setMessages((prev) => [...prev, msg]);
      }
    });

    // Cleanup socket listener on unmount
    return () => {
      socket.off('newMessage');
    };
  }, [messages, selectedConversationId]);
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Initial fetch
    api.get('/conversations').then((res) => {
      setConversations(res.data);
      if (res.data.length) {
        setSelectedConversationId(res.data[0].id); // auto-select first
      }
    });

    api.get('/users').then((res) => setAllUsers(res.data));
    
  }, []);

  const formatSize = (size: number) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const truncateFileName = (name: string, maxLength: number) => {
    if (name.length <= maxLength) return name;

    const extIndex = name.lastIndexOf('.');
    if (extIndex === -1) return name.slice(0, maxLength) + '...';

    const namePart = name.slice(0, extIndex);
    const ext = name.slice(extIndex);

    const shownStart = namePart.slice(0, maxLength - ext.length - 5);
    return `${shownStart}.....${ext}`;
  };
  
  const selectedConversation = conversations.find(c => c.id === selectedConversationId);
 return (
  <div className="flex h-screen text-gray-900">
    {/* Sidebar */}
    <aside className="w-64 bg-white border-r shadow p-4 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Conversations</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-gray-200 hover:bg-gray-300 text-sm px-3 py-1 rounded"
        >
          + New
        </button>
      </div>

      <div className="space-y-2 overflow-y-auto">
        {conversations.map((c) => (
          <div
            key={c.id}
            onClick={() => setSelectedConversationId(c.id)}
            className={`p-2 rounded-lg cursor-pointer font-medium ${
              selectedConversationId === c.id
                ? 'bg-blue-600 text-white'
                : 'hover:bg-gray-100'
            }`}
          >
            {c.title}
          </div>
        ))}
      </div>

      

    </aside>

    {/* Main Chat Window */}
    <main className="flex-1 flex flex-col bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-white shadow">
        <div className="text-lg font-semibold">
          {selectedConversation?.title || 'Select a conversation'}
        </div>

        <div className="relative flex justify-end items-center">
          <div
            onClick={() => setOpen(!open)}
            className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center cursor-pointer text-sm font-bold"
          >
            {initials}
          </div>

          {open && (
            <div className="absolute right-0 top-12 bg-white border rounded shadow-md w-48 z-50">
              <div className="px-4 py-2 text-sm text-gray-800 font-semibold border-b">{login_user?.name}</div>
              <button
                onClick={() => alert('Settings page coming soon!')}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
              >
                Settings
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  delete axios.defaults.headers.common['Authorization'];
                  onLogout();
                  navigate('/');
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
      

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto space-y-4"
        ref={messagesContainerRef}
        onScroll={handleScroll}
        >
        {messages.map((msg, index) => {
          const isCurrentUser = msg.sender?.id === senderId;
          const prevMsg = messages[index - 1];
          const isSameSenderAsPrevious = prevMsg && prevMsg.sender?.id === msg.sender?.id;
          return(
          <div
            key={msg.id}
            ref={(el) => {
              if (el) messageRefs.current[msg.id] = el;
            }}            
            className={`w-fit max-w-md p-2 rounded-lg shadow-md ${
              msg.sender?.id === senderId ? 'ml-auto bg-blue-600 text-white' : 'mr-auto bg-gray-200'
            }`}
          >
            {/* Show name only if first in block */}
            {!isSameSenderAsPrevious && (
              <div className="text-sm font-semibold">{msg.sender?.name}</div>
            )}

            {msg.replyTo && (
              <div 
              onClick={() => setReplyScrollTarget(msg.replyTo?.id as number)} 
              className="bg-black bg-opacity-30 text-sm text-white-600 border-l-2 p-2 border-white-500 mb-1">
                <span className="font-medium">{msg.replyTo.sender?.name || 'Unknown'}</span>
                <p className="text-xs text-white-500 truncate">{msg.replyTo.content}</p>
              </div>
            )}
            
            {msg.attachments?.map((att) => {
              const ext = att.fileUrl.split('.').pop()?.toLowerCase();

              if (att.fileUrl.match(/\.(jpg|jpeg|png|gif)$/i)) {
                return (
                  <div key={att.id}>
                    <a href={`http://localhost:3000${att.fileUrl}`} data-fancybox="gallery">
                      <img
                        src={`http://localhost:3000${att.fileUrl}`}
                        alt="attachment"
                        className="max-w-72 min-w-50 aspect-square rounded mb-2 object-cover"
                      />
                    </a>
                  </div>
                );
              } else if (att.fileUrl.match(/\.(mp4|webm)$/i)) {
                return (
                  <div key={att.id}>
                    <a href={`http://localhost:3000${att.fileUrl}`} className="relative inline-block group" data-fancybox="gallery" data-caption={att.originalName}>
                      <video
                        
                        className="w-72 rounded shadow"
                        src={`http://localhost:3000${att.fileUrl}`}
                      >
                        Your browser does not support the video tag.
                      </video>
                      {/* Play icon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-70 group-hover:opacity-100 transition">
                        <div className="bg-black bg-opacity-60 p-2 rounded-full text-white text-xl">
                          ▶️
                        </div>
                      </div>
                    </a>
                    <div className="flex-1">
                      {/* <div className="text-sm font-medium truncate">
                        {truncateFileName(att.originalName, 24)}
                      </div> */}
                      <div className="text-xs text-white-600">{formatSize(att.size)}, {ext?.toUpperCase()} File</div>
                    </div>
                    <div className="flex justify-between my-2 gap-2">
                      <a href={`http://localhost:3000/messages/download/${att.fileUrl.split('/').pop()}`} download={att.originalName} className="flex-1 text-center bg-black bg-opacity-20 text-white text-sm font-medium py-1">
                        Download
                      </a>
                    </div>
                  </div>
                );
              } else {
                return (
                  <FileAttachmentCard
                    key={att.id}
                    fileName={att.originalName}
                    fileSize={att.size}
                    fileUrl={att.fileUrl}
                  />
                );
              }
            })}


            {/* 💬 Message content */}
            {msg.content && <div className="text-base mb-2">{msg.content}</div>}

            <div className="flex justify-between gap-5">            
              <button 
                onClick={() => setReplyTo(msg)} 
                className="text-xs text-blue-200 mt-1 hover:underline"
              >
                <FontAwesomeIcon icon={faReply} /> Reply
              </button>
              <div className="text-xs mt-1 text-right opacity-60">
                {new Date(msg.createdAt).toLocaleTimeString()}
              </div>
            </div>

            
          </div>
      )})}

        
        <div ref={messagesEndRef} />
      </div>
      {newMessagesCount > 0 && (
        <button
          onClick={() => {
            scrollToBottom();
            setNewMessagesCount(0);
          }}
          className="fixed bottom-24 right-6 flex items-center gap-2 bg-blue-600 text-white text-sm pr-4 pl-2 py-2 rounded-full shadow-md hover:bg-blue-700 transition z-50"
        >
          <div className="bg-white text-black rounded-full px-2 min-w-6 h-6 flex items-center justify-center text-xs font-bold">
            {newMessagesCount}
          </div>
          New Message
        </button>
      )}

      <ChatInput
          selectedConversationId={selectedConversationId || 0}
          senderId={senderId || 0}
          setMessages={setMessages}
          replyTo={replyTo}
          setReplyTo={setReplyTo}
        />
      
    </main>

    {showModal && (
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div className="bg-white rounded p-6 w-full max-w-md shadow-lg">
          <h2 className="text-xl font-bold mb-4">Create Conversation</h2>

          <input
            type="text"
            placeholder="Conversation title"
            className="w-full border px-3 py-2 rounded mb-3"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />

          <div className="max-h-40 overflow-y-auto border p-2 rounded mb-3">
            <p className="text-sm font-semibold mb-1">Select participants:</p>
            {allUsers.filter(user => user.id !== senderId).map((user) => (
              
              <label key={user.id} className="flex items-center space-x-2 text-sm mb-1">
                <input
                  type="checkbox"
                  value={user.id}
                  checked={selectedUsers.includes(user.id)}
                  onChange={(e) => {
                    const id = parseInt(e.target.value);
                    setSelectedUsers((prev) =>
                      e.target.checked
                        ? [...prev, id]
                        : prev.filter((uid) => uid !== id)
                    );
                  }}
                />
                <span>{user.name}</span>
              </label>
            ))}
          </div>

          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-sm border rounded"
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded"
              onClick={async () => {
                const res = await api.post('/conversations', {
                  title: newTitle,
                  type: 'group',
                  participants: selectedUsers,
                });
                setConversations((prev) => [...prev, res.data]);
                setSelectedConversationId(res.data.id);
                setShowModal(false);
                setNewTitle('');
                setSelectedUsers([]);
              }}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    )}

  </div>

  
);



}

export default Chat;
